/**
 * Canonical Supabase Storage bucket ids and the mechanical path-translation
 * rule for the "Storage consolidation: 3-bucket model" project (ENT-3338 = Phase 0).
 *
 * Single source of truth, shared across both runtimes. The canonical content
 * lives HERE, inside the Supabase functions source root, so the API-mode edge
 * bundler (`supabase functions deploy --use-api`) attaches it to every importing
 * function instead of skipping it as an out-of-source-root path (ENT-3501):
 *   - Deno (supabase/**): import { LEGACY_STORAGE_BUCKETS } from '../_shared/storage-buckets.ts';
 *   - Web (src/**):       import { LEGACY_STORAGE_BUCKETS } from '@/lib/storageBuckets';
 *     (a pure re-export shim under `@/lib` forwards to this file; web callers
 *      are unchanged).
 *
 * Because there is ONE module (no web/Deno twin), no cross-module parity test is
 * needed — both runtimes import these same constants. Keep this module
 * dependency-free (pure constants + path helper); do not add imports to it.
 *
 * Phase 0 is a PURE refactor: every call site still targets the same bucket it
 * did before; only the scattered string literals move here. Do not change which
 * bucket any caller uses, rename a bucket, or touch storage RLS in Phase 0.
 */

/**
 * The 14 feature-scoped buckets that exist today. `telelens-briefings` and
 * `telelens-imports` currently exist in staging only (ahead of production).
 * Keys are SCREAMING_SNAKE to match `CONSOLIDATED_STORAGE_BUCKETS`.
 */
export const LEGACY_STORAGE_BUCKETS = {
  AVATARS: 'avatars',
  SUPPLIER_LOGOS: 'supplier-logos',
  CERTIFICATE_LOGOS: 'certificate-logos',
  ORG_LOGOS: 'org-logos',
  MARKETING_ASSETS: 'marketing-assets',
  SUPPLIER_DOCUMENTS: 'supplier-documents',
  POAM_EVIDENCE: 'poam-evidence',
  CERTIFICATES: 'certificates',
  BREACH_EVIDENCE: 'breach-evidence',
  SUPPLIER_ASSURANCE_DOCUMENTS: 'supplier-assurance-documents',
  PITCH_BOOK_PDF_EXPORTS: 'pitch-book-pdf-exports',
  SUPPLY_CHAIN_BRIEFING_SNAPSHOTS: 'supply-chain-briefing-snapshots',
  TELELENS_BRIEFINGS: 'telelens-briefings',
  TELELENS_IMPORTS: 'telelens-imports',
} as const;

export type LegacyStorageBucketId =
  (typeof LEGACY_STORAGE_BUCKETS)[keyof typeof LEGACY_STORAGE_BUCKETS];

/**
 * The three consolidated buckets encoding durability + exposure class
 * (public-assets / documents / cache). UNUSED in Phase 0 — introduced here so
 * that the Phase 2 per-domain flips change a constant rather than hunting string
 * literals again. See project "Storage consolidation: 3-bucket model".
 */
export const CONSOLIDATED_STORAGE_BUCKETS = {
  PUBLIC_ASSETS: 'public-assets',
  DOCUMENTS: 'documents',
  CACHE: 'cache',
} as const;

export type ConsolidatedStorageBucketId =
  (typeof CONSOLIDATED_STORAGE_BUCKETS)[keyof typeof CONSOLIDATED_STORAGE_BUCKETS];

/**
 * Mechanical translation rule (project invariant #1): the consolidated object
 * path is `<legacy-bucket-id>/<legacy-path>` — the legacy bucket id becomes the
 * top-level folder and the sub-path is preserved byte-for-byte. Leading slashes
 * on the legacy path are normalized away so the result never contains a `//`
 * seam (e.g. insurance paths like `/vendor/<id>/insurance/x.pdf`).
 *
 * Pure string helper: it does NOT move objects or change any bucket. Unused by
 * callers until Phase 2; exported and unit-tested now so the rule lives in one
 * place.
 */
export function toConsolidatedPath(legacyBucketId: string, legacyPath: string): string {
  const normalizedPath = legacyPath.replace(/^\/+/, '');
  return `${legacyBucketId}/${normalizedPath}`;
}
